import { Children, cloneElement, isValidElement } from "react";

import InlineError from "./InlineError";

// Field — presentation primitive. Wraps a single input child with a
// label-above-input layout, help-text slot, error slot, and a11y wiring
// (aria-describedby / aria-invalid).
//
// Field owns: label/help/error layout, ID derivations, aria wiring.
// Field does NOT own: validation, mutation, submit, Redux, API, or any
// feature knowledge. See migration/phase9-text-form-plan.md §3.1.
//
// Props:
//   id:        string — required; root id for the field (child input
//              inherits via id, help via `${id}-help`, error via
//              `${id}-error`)
//   label:     node   — visible label text
//   help:      node   — optional help text below label
//   error:     node   — optional error text below input
//   required:  bool   — render a required marker on the label
//   className: string — applied to the wrapper
//   children:  single React element — the input (typically TextInput)

function Field({
  id,
  label,
  help,
  error,
  required = false,
  className = '',
  children,
}) {
  const child = Children.only(children);

  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);

  const enhancedChild = isValidElement(child)
    ? cloneElement(child, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': invalid ? 'true' : undefined,
      })
    : child;

  const classes = [
    'ui-field',
    invalid ? 'ui-field--invalid' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <label htmlFor={id} className="ui-field__label">
        {label}
        {required ? (
          <span className="ui-field__required" aria-hidden="true">*</span>
        ) : null}
      </label>

      {help ? (
        <span id={helpId} className="ui-field__help">
          {help}
        </span>
      ) : null}

      <div className="ui-field__control">{enhancedChild}</div>

      {error ? (
        <InlineError id={errorId} className="ui-field__error">
          {error}
        </InlineError>
      ) : null}
    </div>
  );
}

export default Field;
